import { Globe2 } from "lucide-react";
import { SUPPORTED_LANGS, useLang } from "@/contexts/LangContext";

/**
 * Reusable language picker for the /concierge, /government-services, /services pages.
 * Drop-in component — uses the shared LangContext so a switch on one page sticks
 * across navigation. Variant prop:
 *   - "dark"  (default): white text on ocean-deep gradient hero (used on hero sections)
 *   - "light": foreground text on card background (used on inline / footer placements)
 */
export default function LanguagePicker({ variant = "dark", className = "", testId = "page-lang-select" }) {
    const { lang, setLang } = useLang();
    const isDark = variant === "dark";

    return (
        <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${
                isDark
                    ? "bg-white/5 border border-white/15"
                    : "bg-secondary/40 dark:bg-card/60 border border-border"
            } ${className}`}
            data-testid={`${testId}-wrapper`}
        >
            <Globe2 className={`h-3.5 w-3.5 ${isDark ? "text-turquoise" : "text-coral"}`} />
            <label htmlFor={testId} className={`text-[10px] uppercase tracking-wider font-app mr-1 ${
                isDark ? "text-white/70" : "text-muted-foreground"
            }`}>
                Language
            </label>
            <select
                id={testId}
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                data-testid={testId}
                className={`bg-transparent text-sm font-app focus:outline-none cursor-pointer ${
                    isDark ? "text-white" : "text-foreground"
                }`}
                aria-label="Choose page language"
            >
                {SUPPORTED_LANGS.map((l) => (
                    <option
                        key={l.code}
                        value={l.code}
                        className={isDark ? "bg-ocean-deep text-white" : "bg-background text-foreground"}
                    >
                        {l.label}
                    </option>
                ))}
            </select>
        </div>
    );
}
